/**
 * O que faz: Modal de exportação do relatório SEO com toggle wide/vertical e impressão via window.print.
 * Onde usa: SeoProfileResult (botão "Exportar relatório").
 * Conexões: SeoProfileReport produz os slides; @media print + @page controlam o tamanho da página.
 * Tabelas: Não se aplica.
 * Data: 14/05/2026
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Monitor, Printer, Smartphone } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { SeoProfileAnalysisResult } from '@/types/seoProfile';
import { SeoProfileReport, type ReportFormat } from './SeoProfileReport';

interface SeoProfileExportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  analysis: SeoProfileAnalysisResult;
  clinicName?: string;
}

const PAGE_DIMS = {
  wide: { w: 1920, h: 1080, inW: 20, inH: 11.25 },
  vertical: { w: 1080, h: 1920, inW: 11.25, inH: 20 },
};

export function SeoProfileExportDialog({ open, onOpenChange, analysis, clinicName }: SeoProfileExportDialogProps) {
  const [format, setFormat] = useState<ReportFormat>('wide');
  const previewWrapperRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(0.3);

  const dim = PAGE_DIMS[format];

  // Compute scale so preview width fits available space
  useEffect(() => {
    if (!open) return;
    const update = () => {
      const wrap = previewWrapperRef.current;
      if (!wrap) return;
      const avail = wrap.clientWidth - 32;
      const next = Math.min(0.5, avail / dim.w);
      setScale(Math.max(0.15, next));
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, [open, format, dim.w]);

  // Inject dynamic @page rule for the chosen format
  const printStyles = useMemo(() => {
    return `
      @page {
        size: ${dim.inW}in ${dim.inH}in;
        margin: 0;
      }
      @media print {
        body * { visibility: hidden !important; }
        .seo-print-only { display: block !important; }
        .seo-print-only, .seo-print-only * { visibility: visible !important; }
        .seo-print-only {
          position: absolute !important;
          left: 0 !important;
          top: 0 !important;
          transform: none !important;
          width: auto !important;
          height: auto !important;
        }
        .seo-print-only .seo-slide {
          page-break-after: always;
          break-after: page;
          page-break-inside: avoid;
          break-inside: avoid;
        }
      }
    `;
  }, [dim.inW, dim.inH]);

  const handlePrint = () => {
    window.print();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl p-0 overflow-hidden">
        <DialogHeader className="px-6 pt-6 pb-4 border-b">
          <DialogTitle className="text-xl">Exportar relatório</DialogTitle>
          <div className="flex flex-col gap-3 pt-2 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-2 rounded-lg bg-muted p-1">
              <button
                type="button"
                onClick={() => setFormat('wide')}
                className={cn(
                  'flex items-center gap-2 rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
                  format === 'wide' ? 'bg-white text-emerald-700 shadow-sm' : 'text-muted-foreground hover:text-foreground',
                )}
              >
                <Monitor className="h-4 w-4" />
                Wide (apresentação)
              </button>
              <button
                type="button"
                onClick={() => setFormat('vertical')}
                className={cn(
                  'flex items-center gap-2 rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
                  format === 'vertical' ? 'bg-white text-emerald-700 shadow-sm' : 'text-muted-foreground hover:text-foreground',
                )}
              >
                <Smartphone className="h-4 w-4" />
                Vertical (celular)
              </button>
            </div>

            <Button onClick={handlePrint} className="gap-2 bg-emerald-600 hover:bg-emerald-700">
              <Printer className="h-4 w-4" />
              Imprimir / Salvar PDF
            </Button>
          </div>
        </DialogHeader>

        <div className="bg-neutral-100 dark:bg-neutral-900 max-h-[70vh] overflow-auto p-4">
          <div ref={previewWrapperRef} className="flex flex-col items-center gap-4">
            <div
              style={{
                width: `${dim.w * scale}px`,
                height: `${dim.h * scale}px`,
                overflow: 'hidden',
                position: 'relative',
              }}
            >
              <div
                style={{
                  transform: `scale(${scale})`,
                  transformOrigin: 'top left',
                  width: `${dim.w}px`,
                }}
              >
                <SeoProfileReport analysis={analysis} format={format} clinicName={clinicName} />
              </div>
            </div>
          </div>
        </div>

        <p className="px-6 pb-5 pt-3 text-xs text-muted-foreground">
          Dica: no diálogo de impressão, escolha "Salvar como PDF", desmarque cabeçalhos/rodapés e mantenha margens em "Nenhuma" para preservar o layout.
        </p>

        <style>{printStyles}</style>

        {/* Hidden full-size copy — only made visible during print via @media print */}
        <div
          aria-hidden="true"
          className="seo-print-only"
          style={{
            display: 'none',
          }}
        >
          <SeoProfileReport analysis={analysis} format={format} clinicName={clinicName} />
        </div>
      </DialogContent>
    </Dialog>
  );
}
